// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import type {ColorMode} from '@primer/react-brand/esm'
import {BookIcon, ListUnorderedIcon, MarkGithubIcon, McpIcon, PencilIcon, RepoIcon} from '@primer/octicons-react'
import {OS} from '@github-ui/get-os'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import ImageFeatures1Windows from './assets/images/features_1-windows.webp'
import ImageFeatures1Mac from './assets/images/features_1-mac.webp'
import ImageFeatures2Windows from './assets/images/features_2-windows.webp'
import ImageFeatures2Mac from './assets/images/features_2-mac.webp'
import ImageFeatures3Windows from './assets/images/features_3-windows.webp'
import ImageFeatures3Mac from './assets/images/features_3-mac.webp'
import ImageFeatures4Windows from './assets/images/features_4-windows.webp'
import ImageFeatures4Mac from './assets/images/features_4-mac.webp'
import VideoHero1Windows from './assets/videos/hero_1-windows.mp4'
import VideoHero1Mac from './assets/videos/hero_1-mac.mp4'
import VideoHero1Mobile from './assets/videos/hero_1-mobile.mp4'
import VideoHero2Windows from './assets/videos/hero_2-windows.mp4'
import VideoHero2Mac from './assets/videos/hero_2-mac.mp4'
import VideoHero2Mobile from './assets/videos/hero_2-mobile.mp4'
import VideoHero3Windows from './assets/videos/hero_3-windows.mp4'
import VideoHero3Mac from './assets/videos/hero_3-mac.mp4'
import VideoHero3Mobile from './assets/videos/hero_3-mobile.mp4'
import VideoHero4Windows from './assets/videos/hero_4-windows.mp4'
import VideoHero4Mac from './assets/videos/hero_4-mac.mp4'
import VideoHero4Mobile from './assets/videos/hero_4-mobile.mp4'

import {PLAN_TYPES} from '../../../../brand/components/PlanTypeSegmentedControl/PlanTypeSegmentedControl'
import type {HeroProps} from './components/Hero/Hero'
import type {CardsSectionProps} from './components/CardsSection/CardsSection'
import type {FeaturesProps} from './components/Features/Features'
import type {HighlightProps} from './components/Highlight/Highlight'
import type {InstallerItem} from './components/InstallerActionMenu/InstallerActionMenu'
import type {
  PricingPlan,
  PricingPlanFeatureItem,
  PricingPlanFeatureItemGroupHeading,
  PricingProps,
} from './components/Pricing/Pricing'
import type {CtaBannerProps} from './components/CtaBanner/CtaBanner'

export type {Visual, VisualData, VisualResolved, VisualSrc} from './Index.types'

export interface GitHubAppGAPageCopy {
  subNav: {
    heading: {
      label: string
      href: string
    }
    items: Array<{
      label: string
      href: string
      isSelected?: boolean
    }>
  }
  hero: HeroProps
  features: FeaturesProps
  highlight: HighlightProps
  tools: CardsSectionProps
  pricing: Omit<PricingProps, 'plans'> & {
    plans: Array<
      Omit<PricingPlan, 'footnote' | 'actionsMessage' | 'features'> & {
        footnote?: string
        actionsMessage?: Omit<PricingPlan['actionsMessage'], 'content'> & {content: string}
        features: Omit<PricingPlan['features'], 'items'> & {
          items: Array<
            | (Omit<PricingPlanFeatureItem, 'label'> & {label: string})
            | (Omit<PricingPlanFeatureItemGroupHeading, 'label'> & {label: string})
          >
        }
      }
    >
  }
  ctaBanner: CtaBannerProps
  resources: CardsSectionProps
}

const DOWNLOAD_ITEMS: InstallerItem[] = [
  {
    platform: OS.macOS,
    arch: 'arm',
    label: 'Download for macOS (Silicon)',
    href: 'https://gh.io/copilot-app-mac',
  },
  {
    platform: OS.macOS,
    arch: 'x86',
    label: 'Download for macOS (Intel)',
    href: 'https://gh.io/copilot-app-mac-intel',
  },
  {
    platform: OS.Windows,
    arch: 'x86',
    label: 'Download for Windows (x64)',
    href: 'https://gh.io/copilot-app-win64',
  },
  {
    platform: OS.Windows,
    arch: 'arm',
    label: 'Download for Windows (ARM)',
    href: 'https://gh.io/copilot-app-win-arm',
  },
  {
    platform: OS.Linux,
    label: 'Download for Linux',
    href: 'https://gh.io/copilot-app-linux',
  },
]

export const PRICING_PLANS: GitHubAppGAPageCopy['pricing']['plans'] = [
  // Individual plans
  {
    type: PLAN_TYPES.Individual,
    name: 'Free',
    description: 'For getting started with GitHub Copilot.',
    price: {
      value: 0,
    },
    features: {
      heading: "What's included:",
      items: [
        {
          type: 'item',
          label: '2,000 completions per month',
        },
        {
          type: 'item',
          label: 'Copilot CLI',
        },
        {
          type: 'item',
          label: 'GitHub Copilot app',
        },
      ],
    },
    cta1: {
      label: 'Get started',
      href: 'https://github.com/copilot',
    },
    footnote:
      'No credit card required. Verified students have access to the GitHub Copilot Student plan. [Learn more](https://github.com/education/students)',
  },
  {
    type: PLAN_TYPES.Individual,
    name: 'Pro',
    description: 'For everyday coding with agents in GitHub Copilot.',
    price: {
      value: 10,
      trailingText: 'per user / month',
    },
    features: {
      heading: 'Everything from Free and:',
      items: [
        {
          type: 'item',
          label: 'Access to Cloud agent and code review',
          info: 'Code review will incur Actions usage.',
        },
        {
          type: 'item',
          label: 'Unlimited code completion and next edit suggestions',
          info: 'Response times may vary during periods of high usage. Requests may be subject to rate limiting.',
        },
        {
          type: 'item',
          label: 'Access to 3rd party agents (Claude Code and Codex)',
        },
        {
          type: 'item',
          label: 'Model selection',
        },
        {
          type: 'item',
          label: '$15 monthly total credits for Pro',
          info: 'Flex allotments will vary over time.',
        },
      ],
    },
    // TODO: Delete once no longer relevant
    actionsMessage: {
      // Markdown
      content:
        '__New plan sign-ups are temporarily paused as we ensure a high-quality experience.__ Existing Student, Pro, and Pro+ customers can [upgrade to Max](https://github.com/github-copilot/max). We appreciate your patience. [Learn more](https://gh.io/indiv-change-blog)',
    },
  },
  {
    type: PLAN_TYPES.Individual,
    name: 'Pro+',
    description: 'For more complex development with premium models.',
    price: {
      value: 39,
      trailingText: 'per user / month',
    },
    features: {
      heading: 'Everything from Pro and:',
      items: [
        {
          type: 'item',
          label: 'Access to premium models, including Opus',
        },
        {
          type: 'item',
          label: 'Audit logs',
        },
        {
          type: 'item',
          label: '4x+ included usage than Pro',
        },
        {
          type: 'item',
          label: '$70 monthly total credits for Pro+',
          info: 'Flex allotments will vary over time.',
        },
      ],
    },
    // TODO: Delete once no longer relevant
    actionsMessage: {
      // Markdown
      content:
        '__New plan sign-ups are temporarily paused as we ensure a high-quality experience.__ Existing Student, Pro, and Pro+ customers can [upgrade to Max](https://github.com/github-copilot/max). We appreciate your patience. [Learn more](https://gh.io/indiv-change-blog)',
    },
  },
  {
    type: PLAN_TYPES.Individual,
    name: 'Max',
    description: 'For sustained, high-volume agent workflows with GitHub Copilot.',
    price: {
      value: 100,
      trailingText: 'per user / month',
    },
    features: {
      heading: 'Everything in Pro+ and:',
      items: [
        {
          type: 'item',
          label: 'Priority access to new models and features',
        },
        {
          type: 'item',
          label: '2.9x+ included usage than Pro+',
        },
        {
          type: 'item',
          label: '$200 monthly total credits for Pro+',
          info: 'Flex allotments will vary over time.',
        },
      ],
    },
    highlightLabel: 'Best value',
    // TODO: Delete once no longer relevant
    actionsMessage: {
      // Markdown
      content:
        '__New plan sign-ups are temporarily paused as we ensure a high-quality experience.__ Existing Student, Pro, and Pro+ customers can [upgrade to Max](https://github.com/github-copilot/max). We appreciate your patience. [Learn more](https://gh.io/indiv-change-blog)',
    },
  },

  // Business plans
  {
    type: PLAN_TYPES.Business,
    name: 'Business',
    description: 'For teams that want pooled credits and control.',
    price: {
      value: 19,
      trailingText: 'per user / month',
    },
    features: {
      heading: "What's included:",
      items: [
        {
          type: 'item',
          label: 'Unlimited code completion and next edit',
        },
        {
          type: 'item',
          label: 'Access to cloud agent and code review',
          info: 'Code review will incur Actions usage.',
        },
        {
          type: 'item',
          label: 'Access to broad model catalog',
        },
        {
          type: 'item',
          label: 'Access to 3rd party agents',
        },
        {
          type: 'item',
          label: 'Access control, budget control, and governance',
        },
        {
          type: 'item',
          label: 'IP indemnity and data privacy',
        },
        {
          type: 'item',
          label: 'GitHub Copilot app',
        },
      ],
    },
    cta1: {
      label: 'Contact sales',
      href: 'https://github.com/enterprise/contact',
    },
  },
  {
    type: PLAN_TYPES.Business,
    name: 'Enterprise',
    description: 'For org-wide rollout and larger pooled credits.',
    price: {
      value: 39,
      trailingText: 'per user / month',
    },
    features: {
      heading: 'Everything in Business and:',
      items: [
        {
          type: 'item',
          label: 'Priority access to new models and features',
        },
        {
          type: 'item',
          label: '2x included usage than Business',
        },
      ],
    },
    cta1: {
      label: 'Contact sales',
      href: 'https://github.com/enterprise/contact',
    },
    highlightLabel: 'Best value',
  },
]

const SIGNUP_CTA_HREFS: Record<string, string> = {
  Pro: 'https://github.com/github-copilot/pro?cft=copilot_li.copilot_plans.cfi',
  'Pro+': 'https://github.com/github-copilot/pro-plus?cft=copilot_li.copilot_plans.cfi',
  Max: 'https://github.com/github-copilot/max?cft=copilot_li.copilot_plans.cfi',
}

const getPricingPlans = (): GitHubAppGAPageCopy['pricing']['plans'] => {
  if (!isFeatureEnabled('copilot_plans_signups_enabled')) {
    return PRICING_PLANS
  }

  return PRICING_PLANS.map(plan => {
    const href = SIGNUP_CTA_HREFS[plan.name]
    if (!href) return plan

    return {
      ...plan,
      cta1: {label: 'Get started', href},
      actionsMessage: undefined,
    }
  })
}

export const getCopy = (): GitHubAppGAPageCopy => {
  return {
    subNav: {
      heading: {
        label: 'AI',
        href: '/features/ai',
      },
      items: [
        {label: 'GitHub Copilot', href: '/features/copilot'},
        {label: 'Desktop App', href: '/features/ai/github-app', isSelected: true},
      ],
    },
    hero: {
      label: 'GitHub Copilot app',
      heading: 'From issue to merge, in one app',
      osBadges: [
        {iconName: 'apple', label: 'MacOS'},
        {iconName: 'windows', label: 'Windows'},
        {iconName: 'linux', label: 'Linux'},
      ],
      body: 'The GitHub Copilot app is the only desktop experience for agent-driven development built natively on GitHub. Available for macOS, Windows, and Linux, on any Copilot plan, or bring your own key.',
      cta1: {
        items: DOWNLOAD_ITEMS,
      },
      cta2: {
        label: 'Read the docs',
        href: 'https://gh.io/github-app-docs',
      },
      carousel: [
        {
          label: 'Delegate to agents',
          description: 'Start a session from an issue, a prompt, or a PR already in flight.',
          visual: {
            desktop: {
              src: {
                [OS.macOS]: {
                  light: VideoHero2Mac,
                  dark: VideoHero2Mac,
                },
                [OS.Windows]: {
                  light: VideoHero2Windows,
                  dark: VideoHero2Windows,
                },
              },
              alt: 'Screen recording showing the user creating a GitHub Copilot coding session from an issue and watching it edit files, run tests, and open a pull request.',
              type: 'video',
            },
            mobile: {
              src: {
                [OS.macOS]: {
                  light: VideoHero2Mobile,
                  dark: VideoHero2Mobile,
                },
                [OS.Windows]: {
                  light: VideoHero2Mobile,
                  dark: VideoHero2Mobile,
                },
              },
              alt: 'Screen recording showing the user creating a GitHub Copilot coding session from an issue and watching it edit files, run tests, and open a pull request.',
              type: 'video',
            },
          },
        },
        {
          label: 'Centralized inbox',
          description: 'Triage issues and PRs across repos, without leaving the app.',
          visual: {
            desktop: {
              src: {
                [OS.macOS]: {
                  light: VideoHero1Mac,
                  dark: VideoHero1Mac,
                },
                [OS.Windows]: {
                  light: VideoHero1Windows,
                  dark: VideoHero1Windows,
                },
              },
              alt: 'Screen recording showing the user navigating GitHub Copilot from Home to My work, switching repositories, and posting a review comment on a failing pull request.',
              type: 'video',
            },
            mobile: {
              src: {
                [OS.macOS]: {
                  light: VideoHero1Mobile,
                  dark: VideoHero1Mobile,
                },
                [OS.Windows]: {
                  light: VideoHero1Mobile,
                  dark: VideoHero1Mobile,
                },
              },
              alt: 'Screen recording showing the user navigating GitHub Copilot from Home to My work, switching repositories, and posting a review comment on a failing pull request.',
              type: 'video',
            },
          },
        },
        {
          label: 'Shape canvases',
          description:
            'Open a live work surface where you and the agent edit, reorder, and verify the same artifact together.',
          visual: {
            desktop: {
              src: {
                [OS.macOS]: {
                  light: VideoHero4Mac,
                  dark: VideoHero4Mac,
                },
                [OS.Windows]: {
                  light: VideoHero4Windows,
                  dark: VideoHero4Windows,
                },
              },
              alt: 'Screen recording showing the user and Copilot editing and reorganizing content on a shared shape canvas.',
              type: 'video',
            },
            mobile: {
              src: {
                [OS.macOS]: {
                  light: VideoHero4Mobile,
                  dark: VideoHero4Mobile,
                },
                [OS.Windows]: {
                  light: VideoHero4Mobile,
                  dark: VideoHero4Mobile,
                },
              },
              alt: 'Screen recording showing shape canvas interactions in the GitHub Copilot app on mobile.',
              type: 'video',
            },
          },
        },
        {
          label: 'Review and merge',
          description:
            'Review diffs, check CI, and fix failing checks. Merge when you’re ready from the same window the agent works in.',
          visual: {
            desktop: {
              src: {
                [OS.macOS]: {
                  light: VideoHero3Mac,
                  dark: VideoHero3Mac,
                },
                [OS.Windows]: {
                  light: VideoHero3Windows,
                  dark: VideoHero3Windows,
                },
              },
              alt: "Screen recording showing the user reviewing a GitHub Copilot pull request's diff and merging it once checks pass.",
              type: 'video',
            },
            mobile: {
              src: {
                [OS.macOS]: {
                  light: VideoHero3Mobile,
                  dark: VideoHero3Mobile,
                },
                [OS.Windows]: {
                  light: VideoHero3Mobile,
                  dark: VideoHero3Mobile,
                },
              },
              alt: "Screen recording showing the user reviewing a GitHub Copilot pull request's diff and merging it once checks pass.",
              type: 'video',
            },
          },
        },
      ],
      a11y: {
        tabSelector: 'Explore main features',
        playButton: 'Toggle play',
      },
    },
    features: {
      heading: 'Run multiple sessions across every area of work',
      body: 'Open a session from real work across your issues, pull requests, or prompts.',
      items: [
        {
          heading: 'Parallel workflows, fully in view',
          body: 'Take control of your workflow. Every session comes with a workspace for your branches, files, and conversations.',
          cta: {
            label: 'Learn more',
            href: 'https://docs.github.com/en/copilot/how-tos/github-copilot-app/agent-sessions',
          },
          visual: {
            desktop: {
              src: {
                [OS.macOS]: {
                  light: ImageFeatures1Mac,
                  dark: ImageFeatures1Mac,
                },
                [OS.Windows]: {
                  light: ImageFeatures1Windows,
                  dark: ImageFeatures1Windows,
                },
              },
              alt: 'App sidebar with Home, My work, Automations, and Search, plus a Sessions list; a dark-mode icon session is selected.',
              type: 'image',
              position: 'bottom-right',
            },
            mobile: {
              src: {
                [OS.macOS]: {
                  light: ImageFeatures1Mac,
                  dark: ImageFeatures1Mac,
                },
                [OS.Windows]: {
                  light: ImageFeatures1Windows,
                  dark: ImageFeatures1Windows,
                },
              },
              alt: 'App sidebar with Home, My work, Automations, and Search, plus a Sessions list; a dark-mode icon session is selected.',
              type: 'image',
              position: 'bottom-right',
            },
          },
        },
        {
          heading: 'Isolated spaces for every session',
          body: 'Work stays separated, even when you have more than one task in motion.',
          cta: {
            label: 'Learn more',
            href: 'https://docs.github.com/en/copilot/how-tos/github-copilot-app/agent-sessions#starting-a-session',
          },
          visual: {
            desktop: {
              src: {
                [OS.macOS]: {
                  light: ImageFeatures2Mac,
                  dark: ImageFeatures2Mac,
                },
                [OS.Windows]: {
                  light: ImageFeatures2Windows,
                  dark: ImageFeatures2Windows,
                },
              },
              alt: "Dropdown 'Where to run this session' with New worktree (selected) and Local repository options.",
              type: 'image',
            },
            mobile: {
              src: {
                [OS.macOS]: {
                  light: ImageFeatures2Mac,
                  dark: ImageFeatures2Mac,
                },
                [OS.Windows]: {
                  light: ImageFeatures2Windows,
                  dark: ImageFeatures2Windows,
                },
              },
              alt: "Dropdown 'Where to run this session' with New worktree (selected) and Local repository options.",
              type: 'image',
            },
          },
        },
        {
          heading: 'Built-in validation loop',
          body: 'Inspect diffs, preview via in-app browser, run terminal checks, and merge PRs directly inside your session.',
          cta: {
            label: 'Learn more',
            href: 'https://docs.github.com/en/copilot/how-tos/github-copilot-app/managing-issues-and-pull-requests',
          },
          visual: {
            desktop: {
              src: {
                [OS.macOS]: {
                  light: ImageFeatures3Mac,
                  dark: ImageFeatures3Mac,
                },
                [OS.Windows]: {
                  light: ImageFeatures3Windows,
                  dark: ImageFeatures3Windows,
                },
              },
              alt: 'Code diff showing 6 files changed; a single-line TypeScript type export is rewritten as a multi-line export block.',
              type: 'image',
            },
            mobile: {
              src: {
                [OS.macOS]: {
                  light: ImageFeatures3Mac,
                  dark: ImageFeatures3Mac,
                },
                [OS.Windows]: {
                  light: ImageFeatures3Windows,
                  dark: ImageFeatures3Windows,
                },
              },
              alt: 'Code diff showing 6 files changed; a single-line TypeScript type export is rewritten as a multi-line export block.',
              type: 'image',
            },
          },
        },
        {
          heading: 'Automated workflows',
          body: 'Turn skills and prompts into repeatable work that can be run on a regular basis.',
          cta: {
            label: 'Learn more',
            href: 'https://docs.github.com/en/copilot/how-tos/github-copilot-app/using-scheduled-workflows',
          },
          visual: {
            desktop: {
              src: {
                [OS.macOS]: {
                  light: ImageFeatures4Mac,
                  dark: ImageFeatures4Mac,
                },
                [OS.Windows]: {
                  light: ImageFeatures4Windows,
                  dark: ImageFeatures4Windows,
                },
              },
              alt: "New workflow dialog: name 'Morning triage', Daily interval, auto-run enabled, issue-triage prompt, Create button.",
              type: 'image',
            },
            mobile: {
              src: {
                [OS.macOS]: {
                  light: ImageFeatures4Mac,
                  dark: ImageFeatures4Mac,
                },
                [OS.Windows]: {
                  light: ImageFeatures4Windows,
                  dark: ImageFeatures4Windows,
                },
              },
              alt: "New workflow dialog: name 'Morning triage', Daily interval, auto-run enabled, issue-triage prompt, Create button.",
              type: 'image',
            },
          },
        },
      ],
    },
    highlight: {
      a11y: {
        heading: 'Design craft built-in',
      },
      video: {
        src: 'https://www.youtube-nocookie.com/embed/CRSV0euIn7M',
        title: 'How to use the new Impeccable skill in the GitHub Copilot app',
        poster: 'https://i.ytimg.com/vi_webp/CRSV0euIn7M/maxresdefault.webp',
      },
      heading: {
        lead: 'Design craft built-in.',
        body: 'The GitHub Copilot app ships with Impeccable, the open-source design skill, raising the bar for the agent’s output.',
      },
      cta: {
        items: DOWNLOAD_ITEMS,
      },
      items: [
        '**23 design commands.** Use `/critique` for a design review, `/typeset` to fix the typography, `/layout` to adjust spacing, and `/polish` for a final pass.',
        '**Teach it your system once.** Give design system context up front, and the agent remembers your design conventions in the future.',
        '**No complicated setup.** Turn on the skill in Settings under the Experimental tab.',
      ],
    },
    tools: {
      heading: 'Extend agents with your own tools',
      body: 'Your MCP servers, plugins, and skills all come together so you spend less time configuring and more time shipping.',
      items: [
        {
          heading: 'Native GitHub context',
          body: 'Each session natively connects to your code, PRs, issues and search. Deep context without the manual config.',
          cta: {
            label: 'Learn more',
            href: 'https://docs.github.com/en/copilot/how-tos/github-copilot-app/managing-issues-and-pull-requests',
          },
          icon: MarkGithubIcon,
        },
        {
          heading: 'Deploy custom skills',
          body: 'Custom repo and Copilot skills sync automatically across your sessions. Turn any skill into a workflow with one-click.',
          cta: {
            label: 'Learn more',
            href: 'https://docs.github.com/en/copilot/how-tos/github-copilot-app/customize-github-copilot-app',
          },
          icon: PencilIcon,
        },
        {
          heading: 'Connect external data via MCP’s',
          body: "Your repo's MCPs sync automatically. Connect additional custom MCP servers via local or HTTP config.",
          cta: {
            label: 'Learn more',
            href: 'https://docs.github.com/en/copilot/how-tos/github-copilot-app/customize-github-copilot-app',
          },
          icon: McpIcon,
        },
      ],
    },
    pricing: {
      label: 'Plans',
      heading: 'Pricing',
      body: 'Works with every GitHub Copilot plan, or bring your own key.',
      planTypes: [
        {label: 'For individuals', value: 'individual'},
        {label: 'For businesses', value: 'business'},
      ],
      plans: getPricingPlans(),
      a11y: {
        tabSelector: 'Select plan type',
      },
    },
    ctaBanner: {
      label: 'Share your feedback',
      heading: 'Help us build',
      body: "Whether you find a bug or have an idea for a feature, we want to hear from you. Let us know and we'll take your ideas into account.",
      cta1: {
        items: DOWNLOAD_ITEMS,
      },
      cta2: {
        label: 'Share feedback',
        href: 'https://github.com/github/app#feedback-and-issues',
      },
    },
    resources: {
      heading: 'Related resources',
      items: [
        {
          heading: 'Explore documentation',
          body: 'Guides, quick starts, and reference for everything in the app.',
          cta: {
            label: 'Read the docs',
            href: 'https://gh.io/github-app-docs',
          },
          icon: BookIcon,
        },
        {
          heading: 'Check out the repo',
          body: 'Explore the README, file an issue, join in the discussion.',
          cta: {
            label: 'Explore the repo',
            href: 'https://gh.io/github-app-repo',
          },
          icon: RepoIcon,
        },
        {
          heading: 'Keep up-to-date on the latest',
          body: 'Get the latest on new features and fixes as we ship it.',
          cta: {
            label: 'Read the changelog',
            href: 'https://gh.io/github-app-changelogmd',
          },
          icon: ListUnorderedIcon,
        },
      ],
    },
  }
}

export const COLOR_MODE: ColorMode = 'light'
